import React, { useState, useContext, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Container, InputLabel, Select, MenuItem, TextField, Stack, Autocomplete } from '@mui/material';
import { Typography, Alert } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { isEORIUser } from '../../../utils/roles';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const validateDateRange = (name, event, startKey, endKey, setError, searchFilterLocal) => {
  const fromDate = name === startKey ? event : searchFilterLocal[startKey];
  const toDate = name === endKey ? event : searchFilterLocal[endKey];

  if (!fromDate || !toDate) {
    setError('Both dates are required');
    return;
  }

  if (toDate.diff(fromDate, 'month') >= 6) {
    setError(
      name.includes('departure')
        ? 'Departure Date range can not exceed 6 months'
        : 'Arrival Date range can not exceed 6 months'
    );
    return;
  }

  setError('');
};

const SearchPanel = ({
  companies,
  getShipmentData,
  searchFilter,
  setSearchFilter,
  setSelectedCompanyName,
  shipmentStatusIds,
}) => {
  // Initialize selectedCompanyNameLocal from searchFilter if companyId exists
  const getInitialCompanyName = () => {
    if (searchFilter?.companyId && companies?.length > 0) {
      const selectedCompany = companies.find((company) => company.companyId === searchFilter.companyId);
      return selectedCompany?.companyName || 'ALL';
    }
    return 'ALL';
  };

  const [selectedCompanyNameLocal, setSelectedCompanyNameLocal] = useState(getInitialCompanyName());
  const [companyNameTypableValue, setCompanyNameTypableValue] = useState('');

  const [departureDateError, setDepartureDateError] = useState('');
  const [arrivalDateError, setArrivalDateError] = useState('');

  // Initialize local filter state from the passed searchFilter prop
  const [searchFilterLocal, setSearchFilterLocal] = useState(searchFilter);

  // Update local filter state when searchFilter prop changes
  useEffect(() => {
    setSearchFilterLocal(searchFilter);

    // Update selectedCompanyNameLocal when searchFilter changes
    if (searchFilter?.companyId && companies?.length > 0) {
      const selectedCompany = companies.find((company) => company.companyId === searchFilter.companyId);
      setSelectedCompanyNameLocal(selectedCompany?.companyName || 'ALL');
    }
  }, [searchFilter, companies]);

  // Handle case when companies are loaded after component mounts
  useEffect(() => {
    if (companies?.length > 0 && searchFilter?.companyId) {
      const selectedCompany = companies.find((company) => company.companyId === searchFilter.companyId);
      if (selectedCompany) {
        setSelectedCompanyNameLocal(selectedCompany.companyName);
      }
    }
  }, [companies, searchFilter?.companyId]);

  const resetFields = () => {
    setSelectedCompanyNameLocal('ALL');
    setSelectedCompanyName('ALL');
    const newSearchFilter = {
      clientJobNumber: '',
      vehicleIdentity: '',
      lrnMRN: '',
      status: '',
      companyId: null,
      reference: '',
      consignmentRef: '',
      departureStartDate: null,
      departureEndDate: null,
      arrivalStartDate: null,
      arrivalEndDate: null,
    };
    setSearchFilter(newSearchFilter);
    setSearchFilterLocal(newSearchFilter);
    getShipmentData(newSearchFilter, { page: 0 });
    setArrivalDateError('');
    setDepartureDateError('');
  };

  const search = () => {
    const companyName = searchFilter.companyId === null ? 'ALL' : selectedCompanyNameLocal;
    setSelectedCompanyNameLocal(companyName);
    setSelectedCompanyName(companyName);
    setSearchFilter(searchFilterLocal);
    getShipmentData(searchFilterLocal, { page: 0 });
  };

  const handleOnChange = (event, name) => {
    if (event?.$isDayjsObject) {
      if (['departureStartDate', 'departureEndDate'].includes(name)) {
        validateDateRange(
          name,
          event,
          'departureStartDate',
          'departureEndDate',
          setDepartureDateError,
          searchFilterLocal
        );
      } else if (['arrivalStartDate', 'arrivalEndDate'].includes(name)) {
        validateDateRange(name, event, 'arrivalStartDate', 'arrivalEndDate', setArrivalDateError, searchFilterLocal);
      }

      setSearchFilterLocal((prevData) => ({
        ...prevData,
        [name]: event,
      }));
    } else {
      let title = event.target.name;
      let value = event.target.value;
      setSearchFilterLocal((prevData) => ({
        ...prevData,
        [title]: value,
      }));
    }
  };

  return (
    <div className="dashboard-search-wrapper">
      <Container maxWidth="md" className="dashboard-search">
        {/* Sets maximum width, with automatic margins */}
        <Box className="dashboard-search-header">Search</Box>
        <Box sx={{ flexGrow: 1 }} className="dashboard-search-fields-container">
          <Grid spacing={8}>
            <Grid>
              <Stack spacing={1}>
                {isEORIUser() && (
                  <Box className="dashboard-search-field">
                    <Typography variant="body1" className="dashboard-search-field-label">
                      Company
                    </Typography>
                    <Autocomplete
                      value={selectedCompanyNameLocal}
                      fullWidth
                      className="form-input"
                      onChange={(event, value) => {
                        setSearchFilterLocal((prev) => ({
                          ...prev,
                          companyId: value?.companyId || null,
                        }));
                        setSelectedCompanyNameLocal(value?.companyName || '');
                      }}
                      inputValue={companyNameTypableValue}
                      onInputChange={(event, value) => {
                        setCompanyNameTypableValue(value);
                      }}
                      id="controllable-states-demo"
                      options={companies}
                      sx={{ width: 300 }}
                      getOptionLabel={(option) => {
                        return `${option.companyName || option}`;
                      }}
                      renderOption={(renderProps, option) => {
                        const { key, ...optionProps } = renderProps;
                        return (
                          <li key={key} {...optionProps}>
                            {option.companyName || option}
                          </li>
                        );
                      }}
                      renderInput={(params) => <TextField {...params} label="" />}
                    />
                  </Box>
                )}
                <Box className="dashboard-search-field">
                  <Typography variant="body1" className="dashboard-search-field-label">
                    Your Reference
                  </Typography>
                  <TextField
                    id="outlined-required"
                    defaultValue=""
                    fullWidth
                    className="form-input"
                    name="reference"
                    value={searchFilterLocal.reference || ''}
                    onChange={handleOnChange}
                  />
                </Box>
                <Box className="dashboard-search-field">
                  <Typography variant="body1" className="dashboard-search-field-label">
                    Consignment Reference
                  </Typography>
                  <TextField
                    id="outlined-required"
                    defaultValue=""
                    fullWidth
                    className="form-input"
                    name="consignmentRef"
                    value={searchFilterLocal.consignmentRef || ''}
                    onChange={handleOnChange}
                  />
                </Box>
                <Box className="dashboard-search-field">
                  <Typography variant="body1" className="dashboard-search-field-label">
                    ENS LRN/MRN
                  </Typography>
                  <TextField
                    id="outlined-required"
                    defaultValue=""
                    fullWidth // Ensures it remains full width
                    className="form-input"
                    name="lrnMRN"
                    value={searchFilterLocal.lrnMRN || ''}
                    onChange={handleOnChange}
                  />
                </Box>

                <Box className="dashboard-search-field">
                  <Typography variant="body1" className="dashboard-search-field-label">
                    Vehicle Identity
                  </Typography>
                  <TextField
                    id="outlined-required"
                    defaultValue=""
                    fullWidth // Ensures it remains full width
                    className="form-input"
                    name="vehicleIdentity"
                    value={searchFilterLocal.vehicleIdentity || ''}
                    onChange={handleOnChange}
                  />
                </Box>

                <Box className="dashboard-search-field">
                  <Typography variant="body1" className="dashboard-search-field-label">
                    Departure Date
                  </Typography>
                  <div className="dashboard-daterange-picker">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        className="datepicker"
                        defaultValue={null}
                        value={searchFilterLocal.departureStartDate}
                        onChange={(date) => handleOnChange(date, 'departureStartDate')}
                        name="fromDepartureDate"
                        format="DD/MM/YYYY"
                        timezone="UTC"
                      />
                      <DatePicker
                        className="datepicker"
                        defaultValue={null}
                        value={searchFilterLocal.departureEndDate}
                        onChange={(date) => handleOnChange(date, 'departureEndDate')}
                        name="toDepartureDate"
                        format="DD/MM/YYYY"
                        timezone="UTC"
                      />
                    </LocalizationProvider>
                  </div>
                  {departureDateError && <Alert severity="error">{departureDateError}</Alert>}
                </Box>

                <Box className="dashboard-search-field">
                  <Typography variant="body1" className="dashboard-search-field-label">
                    Arrival Date
                  </Typography>
                  <div className="dashboard-daterange-picker">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        className="datepicker"
                        defaultValue={null}
                        value={searchFilterLocal.arrivalStartDate}
                        onChange={(date) => handleOnChange(date, 'arrivalStartDate')}
                        name="fromArrivalDate"
                        format="DD/MM/YYYY"
                        timezone="UTC"
                      />
                      <DatePicker
                        className="datepicker"
                        defaultValue={null}
                        value={searchFilterLocal.arrivalEndDate}
                        onChange={(date) => handleOnChange(date, 'arrivalEndDate')}
                        name="toArrivalDate"
                        format="DD/MM/YYYY"
                        timezone="UTC"
                      />
                    </LocalizationProvider>
                  </div>
                  {arrivalDateError && <Alert severity="error">{arrivalDateError}</Alert>}
                </Box>

                <div className="dashboard-search-field">
                  <Typography variant="body1" className="dashboard-search-field-label">
                    Status
                  </Typography>
                  <Select
                    labelId="submitter-label-2"
                    id="submitter-select-2"
                    value={searchFilterLocal.status || 1}
                    name="status"
                    fullWidth // Ensures it remains full width
                    className="form-input"
                    onChange={handleOnChange}
                  >
                    {shipmentStatusIds?.map((item, index) => (
                      <MenuItem value={item.statusId} key={index}>
                        {item.description}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </Stack>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ flexGrow: 1 }} className="dashboard-search-button-container">
          <button style={{ marginTop: '1rem', marginBottom: '1rem' }} onClick={resetFields}>
            Reset
          </button>
          <button
            style={{ marginTop: '1rem', marginBottom: '1rem' }}
            onClick={search}
            disabled={departureDateError || arrivalDateError}
          >
            {' '}
            Search
          </button>
        </Box>
      </Container>
    </div>
  );
};

export default SearchPanel;
